import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { SelectComponent, SelectChangeEvent } from 'src/app/@core/components/ui/select';
import { PdfViewerComponent } from 'src/app/@shared/components/pdf-viewer/pdf-viewer.component';
import { GeoTechnicalAnalysisReportDataService } from 'src/app/features/geotechnical-analysis/services/geo-technical-analysis-report-data.service';
import { CustomMenuItem } from 'src/app/@core/components/ui/menu/menu.component';
import { SourceNavigationService } from 'src/app/@shared/services/source-navigation.service';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';

/** Preview model for a single report document */
export interface IPreviewReportDocument {
    _id?: string;
    name: string;
    url: string;
    pageNumber?: number;
    /** Timestamp used to force PDF viewer re-renders on the same page */
    _timestamp?: number;
}

/**
 * Right-panel "Reports" tab.
 * Renders a report selector and PDF preview, extracted from the old
 * documentPreviewSide template in geo-technical-analysis-report.component.html.
 * Reads the analysis report via GeoTechnicalAnalysisReportDataService and
 * listens to SourceNavigationService to navigate to specific pages when a
 * source citation is clicked in the AI Dashboard.
 */
@Component({
    selector: 'app-geo-technical-reports-tab',
    standalone: true,
    imports: [CommonModule, FormsModule, SelectComponent, PdfViewerComponent, AssetUrlPipe],
    templateUrl: './reports-tab.component.html',
    styles: [`:host { display: block; height: 100%; width: 100%; }`],
})
export class GeoTechnicalReportsTabComponent implements OnInit, OnDestroy {
    /** Menu options list for the report selector dropdown */
    reportMenuItems: CustomMenuItem[] = [];

    /** Currently previewed report document */
    selectedReportDocument: IPreviewReportDocument | null = null;

    /** Bound value for the select component */
    selectedReportValue: any = null;

    private sourceNavSub?: Subscription;

    constructor(
        public dataService: GeoTechnicalAnalysisReportDataService,
        private sourceNavigationService: SourceNavigationService,
    ) {}

    ngOnInit(): void {
        // Build report menu items from the analysis report loaded by the parent
        this.buildReportMenuItems();

        // Subscribe to source-link navigation events so clicking a citation in the
        // AI Dashboard or Summary tab scrolls to the correct page in this PDF viewer
        this.sourceNavSub = this.sourceNavigationService.navigation$.subscribe(({ label, pageNum }) => {
            this.navigateToSource(label, pageNum);
        });
    }

    ngOnDestroy(): void {
        this.sourceNavSub?.unsubscribe();
    }

    /** Build the dropdown options from the current analysis report's reports list */
    private buildReportMenuItems(): void {
        const report = this.dataService.analysisReport();
        if (!report?.reports?.length) return;

        this.reportMenuItems = report.reports.map(r => ({
            label: r.file ? r.file.name : r.attachment ? r.attachment.filename : r.name ?? '',
            value: r.file ? r.file._id : r.attachment ? r.attachment._id : r._id,
            callback: (item: CustomMenuItem) => {
                this.selectReportById(item.value);
            },
        }));

        // Auto-select the first report on load
        const first = report.reports[0];
        if (first) {
            this.selectedReportDocument = {
                _id: first._id ?? '',
                name: first.file ? first.file.name : first.attachment ? first.attachment.filename : first.name ?? '',
                url: first.url ?? '',
            };
            this.selectedReportValue = this.reportMenuItems[0]?.value ?? null;
        }
    }

    /** Called when the user picks a different report from the dropdown */
    onReportSelectChange(event: SelectChangeEvent): void {
        this.selectReportById(event.value);
    }

    private selectReportById(id: any): void {
        const report = this.dataService.analysisReport();
        const matched = report?.reports?.find(r =>
            r.file ? r.file._id === id : r.attachment ? r.attachment._id === id : r._id === id
        ) ?? null;
        if (!matched) return;
        this.selectedReportDocument = {
            _id: matched._id ?? '',
            name: matched.file ? matched.file.name : matched.attachment ? matched.attachment.filename : matched.name ?? '',
            url: matched.url ?? '',
        };
    }

    /**
     * Fuzzy-matches the source label to a report name and jumps to the
     * specified page number inside the PDF viewer.
     */
    private navigateToSource(label: string, pageNum: string): void {
        const reports = this.dataService.analysisReport()?.reports ?? [];
        if (!reports.length || !label) return;

        const page = parseInt(pageNum.split(/[,\s–—-]+/)[0], 10) || 1;
        const normalize = (s: string) =>
            s.toLowerCase().replace(/<[^>]*>/g, '').replace(/\.[^.]+$/, '').replace(/[()]/g, '').replace(/[\s_-]+/g, ' ').trim();
        const toTokens = (s: string) => normalize(s).split(' ').filter(Boolean);
        const isMatch = (a: string, b: string) => {
            const at = toTokens(a);
            const bt = toTokens(b);
            return at.every(t => bt.includes(t)) || bt.every(t => at.includes(t));
        };

        const matched = reports.find(r => {
            const rawName = r.file?.name ?? r.attachment?.filename ?? r.name ?? '';
            return rawName && isMatch(rawName, label);
        });
        if (!matched) return;

        const name = matched.file?.name ?? matched.attachment?.filename ?? matched.name ?? '';
        const fileId = matched.file?._id ?? matched.attachment?._id ?? matched._id ?? null;
        this.selectedReportDocument = {
            _id: matched._id ?? '',
            name,
            url: matched.url ?? '',
            pageNumber: page,
            _timestamp: Date.now(),
        };
        this.selectedReportValue = fileId;
    }
}
